<script lang="ts">
import CloseButton from '@/components/utils/CloseButton.vue'
import useModal from '@/composables/useModal'
import { KEYCODES } from '@/lib/Constants'
import { getAllFocusableElements } from '@/lib/Utility'
import { disablePageScroll, enablePageScroll } from '@/lib/utils/pageScroll'
import { defineComponent, nextTick, onUnmounted, Ref, ref, watch } from 'vue'

export enum ModalSizes {
  small = 'small',
  medium = 'medium',
  large = 'large'
}

export default defineComponent({
  name: 'Modal',
  components: {
    CloseButton
  },
  inheritAttrs: false,
  props: {
    modalId: {
      type: String,
      required: true
    },
    title: {
      type: String
    },
    size: {
      type: String,
      default: ModalSizes.small
    },
    width: {
      type: String
    },
    maxWidth: {
      type: String
    },
    height: {
      type: String
    },
    maxHeight: {
      type: String
    },
    clickOutsideToClose: {
      type: Boolean,
      default: true
    },
    cssClass: {
      type: Array,
      default: () => []
    },
    closeDisabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['open', 'close'],
  setup(props, context) {
    const { slots, emit } = context
    const modal: Ref<HTMLElement> = ref(null)
    const previouslyActiveElement = ref(null)
    const { isOpen, openModal, closeModal } = useModal(props.modalId)

    const onKeydown = (e: KeyboardEvent) => {
      const modalContents = modal?.value

      // Close out of the modal when user hits the escape key
      if (e.code === KEYCODES.ESC && !props.closeDisabled) {
        e.preventDefault()
        closeModal()
      }

      // If the user is tabbing around, we'll want to trap their focus in the dialog until it's closed
      if (e.code === KEYCODES.TAB) {
        const currentActiveElement = document.activeElement as HTMLElement
        const focusableElements = getAllFocusableElements(modal.value)

        if (!focusableElements.length) {
          e.preventDefault()
          return
        }

        // If the currently active element isn't in the modal, bring it into the modal
        if (!modalContents.contains(currentActiveElement)) {
          e.preventDefault()
          focusableElements[0].focus()
        }
        // If the currently active element is in the modal, trap it within the modal until it's closed
        else {
          const focusedItemIndex = focusableElements.indexOf(currentActiveElement)

          // If the user shift-tabs and is focused on the first item, wrap to the last item
          if (e.shiftKey && focusedItemIndex === 0) {
            e.preventDefault()
            focusableElements[focusableElements.length - 1].focus()
          }

          // If the user tabs past the last element, wrap to the beginning
          if (!e.shiftKey && focusedItemIndex === focusableElements.length - 1) {
            e.preventDefault()
            focusableElements[0].focus()
          }
        }
      }
    }

    const setup = () => {
      // Remember what was focused before the dialog is opened, so we can go back to it on close
      previouslyActiveElement.value = document.activeElement
      disablePageScroll()
      window.addEventListener('keydown', onKeydown)
    }

    const cleanup = async () => {
      enablePageScroll()
      window.removeEventListener('keydown', onKeydown)

      if (previouslyActiveElement.value && previouslyActiveElement.value.focus) {
        nextTick(() => {
          previouslyActiveElement.value.focus()
        })
      }
    }
    const handleStateChanges = async () => {
      if (isOpen.value) {
        setup()
        emit('open', props.modalId)
      } else {
        await cleanup()
        emit('close', props.modalId)
      }
    }

    function onOutsideClick() {
      if (props.clickOutsideToClose && !props.closeDisabled) {
        closeModal()
      }
    }

    watch(isOpen, handleStateChanges)
    onUnmounted(async () => {
      closeModal()
      await cleanup()
    })

    return {
      slots,
      modal,
      isOpen,
      openModal,
      closeModal,
      onOutsideClick
    }
  }
})
</script>

<template>
  <Teleport to="body">
    <div class="background" :class="[cssClass, isOpen && 'open']" @click.self="onOutsideClick">
      <div
        ref="modal"
        :class="['modal', size, isOpen && 'open']"
        :style="{
          width: width,
          'max-width': maxWidth,
          height: height,
          'max-height': maxHeight
        }"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
        aria-labelledby="title"
        :data-testid="`modal-${modalId}`"
      >
        <header>
          <h1 v-if="title" id="title" aria-live="polite">
            {{ title }}
          </h1>
          <slot v-else name="title" />

          <div class="header-actions">
            <slot name="header-actions">
              <CloseButton v-if="!closeDisabled" @click="closeModal()" />
            </slot>
          </div>
        </header>
        <div id="content">
          <slot />
        </div>
        <footer v-if="slots.footer">
          <slot name="footer" />
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
* {
  --background-opacity: 0;
  --modal-overlay-color_default: rgba(0, 0, 0, var(--background-opacity));
  --modal-background-color_default: white;
  --modal-overflow-x_default: hidden;
  --modal-overflow-y_default: auto;
}

.background {
  transition: background-color 0.1s ease-out;
}

.modal {
  display: none;
}

.open {
  --background-opacity: 0.5;
  &.background {
    position: fixed;
    background: var(--modal-overlay-color, var(--modal-overlay-color_default));
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    /*
      The teleport to <body> puts this last in the document, which is only enough to paint above
      elements that carry no z-index of their own. Page chrome carries plenty: the navigation is
      at 1030, the mobile popup menu at 1033, and page content reaches 1000 (dropdowns, the page
      size selector) — and a stacking context is made by z-index alone on a flex item, with no
      position, which is what the notifications page header does at z-index 1. Any one of them
      paints over a dialog left at auto, whatever its DOM position. 1040 clears them all.
      Overridable by a --modal-z-index on the cssClass a caller passes.
    */
    z-index: var(--modal-z-index, 1040);
  }

  &.modal {
    display: flex;
    flex-direction: column;
    background-color: var(--modal-background-color, var(--modal-background-color_default));
    padding: 0.75rem;
    > *:not(:last-child) {
      margin-bottom: 0.5rem;
    }
    header {
      display: flex;
      align-items: center;
      #title {
        position: sticky;
        top: 0;
        margin: 1rem 0;
        font-family: 'Gilroy Bold', sans-serif;
        color: #333333;
        font-size: 1.5rem;
      }
      .header-actions {
        margin-left: auto;
        height: 100%;
      }
    }

    .header-actions,
    footer {
      display: flex;
      margin-inline-start: auto;
      gap: 0.75rem;
    }

    #content {
      width: 100%;
      height: 100%;
      overflow-y: var(--modal-overflow-y, var(--modal-overflow, var(--modal-overflow-y_default)));
      overflow-x: var(--modal-overflow-x, var(--modal-overflow, var(--modal-overflow-x_default)));
      display: var(--modal-content-display, auto);
    }
  }
}

.modal.small {
  width: clamp(300px, 430px, 35vw);
  height: auto;
  max-height: 55vh;
}

.modal.medium {
  width: clamp(300px, 1000px, 65vw);
  height: clamp(300px, 600px, 60vh);
}

.modal.large {
  width: clamp(300px, 2000px, 85vw);
  height: clamp(300px, 800px, 80vh);
}
</style>
